import { lazy, PropsWithChildren, Suspense, useEffect } from 'react';

import {
  Route,
  Routes as RoutesContainer,
  To,
  useLocation,
} from 'react-router';

import { toRelativeUrl } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { useAtom } from 'jotai';

import { getAuthFromNative, setReturnTo } from '@/utils/auth';

import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import Redirect from '@/components/Redirect';

import Constants from 'constants/index';

import { appInitializedAtom } from './state';

import CustomLoginCallback from '@/okta/CustomLoginCallback';
import Admin from '@/routes/Admin';
import EditCategory from '@/routes/Admin/components/EditCategory';
import SignalDocument from '@/routes/Document/SignalDocument';
import DocumentLayout from '@/routes/Layout/DocumentLayout';
import ExploreLayout from '@/routes/Layout/ExploreLayout';
import PlansLayout from '@/routes/Layout/PlansLayout';
import ReportsLayout from '@/routes/Layout/ReportsLayout';
import PrintView from '@/routes/PrintView/PrintView';
import ReportsPlansTab from '@/routes/Reports/Plans/Tab/ReportsPlansTab';
import ReportsTeamMembersTab from '@/routes/Reports/TeamMembers/ReportsTeamMembersTab';
import ReportsTeamMembersView from '@/routes/Reports/TeamMembers/ReportsTeamMembersView';
import SearchResultsPage from '@/routes/SearchResults/SearchResultsPage';
import EditPlanView from '@/routes/TrainingPlans/BuildPlans/EditPlanView';
import BuildQuizzesTab from '@/routes/TrainingPlans/BuildQuizzes/BuildQuizzesTab';
import BuildQuizzesView from '@/routes/TrainingPlans/BuildQuizzes/BuildQuizzesView';
import AddReadOnlyPlanPage from '@/routes/TrainingPlans/ManagePlans/AddReadOnlyPlanPage';
import ManagePlansTab from '@/routes/TrainingPlans/ManagePlans/Tab/ManagePlansTab';
import TeamMemberProgressView from '@/routes/TrainingPlans/ManagePlans/Tab/TeamMemberProgressView';
import PlanAssignees from '@/routes/TrainingPlans/ManagePlans/View/PlanAssignees';
import AssignedPlanView from '@/routes/TrainingPlans/MyPlans/AssignedPlanView';
import DocumentProxy from '@/routes/TrainingPlans/MyPlans/DocumentProxy';
import MyPlansTab from '@/routes/TrainingPlans/MyPlans/MyPlansTab';
import { TrainingMode } from '@/routes/TrainingPlans/TrainingMode/TrainingMode';

const NotFoundPage = lazy(() => import('@/routes/404/404'));
const OperationsSubcategoryPage = lazy(
  () => import('@/routes/Explore/Operations/SubcategoryPage'),
);
const SettingsPage = lazy(() => import('@/routes/Settings/SettingsPage'));
const DocumentRouteHandler = lazy(
  () => import('@/routes/Document/DocumentRouteHandler'),
);

const OperationsTab = lazy(
  () => import('@/routes/Explore/Operations/OperationsTab'),
);

const Placeholder: React.FC = () => <span />;

// App.js has Datadog ErrorBoundary that shows Generic error popup
const NotFoundSwitch: React.FC<PropsWithChildren> = ({ children }) => (
  <RoutesContainer>
    {children}
    <Route path="*" element={<NotFoundPage />} />
  </RoutesContainer>
);

const oldPathsForRedirects: Record<string, To> = {
  '/training': '/plans/my-plans',
  '/training/build': '/plans/manage-plans',
  '/training/plan/:planId': '/plans/manage-plans/:planId/edit',
  '/training/assigned/toComplete': '/plans/my-plans',
  '/training/assigned/:planId': '/plans/my-plans/:planId',
  '/training/quiz/:quizId': '/plans/manage-quizzes/:quizId',
  '/training/:planId/team-members/progress/:teamMemberId':
    '/plans/manage-plans/:planId/team-member/:teamMemberId',
  '/training/:planId/team-members': '/plans/manage-plans/:planId',
  '/training/team': '/plans/manage-plans',
  '/training/quizzes': '/plans/manage-quizzes',
  '/training/quizzes/build': '/plans/manage-quizzes/new',
  '/profile': '/settings',
  '/reports/:planId/team-members/progress/:teamMemberId':
    '/reports/plans/:planId/team-member/:teamMemberId',
  '/reports/compliance': '/reports/plans',
  '/reports/plans/progress/:planId/': '/reports/plans/:planId',
  '/reports/plans/progress/:planId/:compliancePlan?':
    '/reports/plans/:planId/:compliancePlan?',
  '/reports/team-members/progress/:teamMemberId':
    '/reports/team-members/:teamMemberId',
  'reports/:planId/team-members/progress/:teamMemberId':
    '/reports/team-members/:teamMemberId/plan/:planId',
};

/**
 * Custom SecureRoute to replace SecureRoute from okta-react
 * react-router v7 Routes only support its own Route as direct children
 **/
const SecureRoute = ({ children }: PropsWithChildren) => {
  const { authState, oktaAuth } = useOktaAuth();

  useEffect(() => {
    if (authState && !authState.isAuthenticated) {
      setReturnTo();
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin,
      );
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect();
    }
  }, [authState, oktaAuth]);

  // Show loading while auth state is being determined
  if (!authState) {
    return <LoadingOverlay isOpen />;
  }

  return authState.isAuthenticated ? children : null;
};

// Native does not use the okta <Security> wrapper but checks for auth via useOktaAuth hook
const SecureRoutesHandledByNative: React.FC = () => (
  <Suspense fallback={<Placeholder />}>
    <NotFoundSwitch>
      <Route path={`/settings`} element={<SettingsPage />} />
      <Route
        path={`/${Constants.ROUTE_PATH_NAMES.BINARY_PATH_NAME}/:documentId`}
        element={<PrintView />}
      />
      <Route
        path="/plans/manage-plans/:planId/edit"
        element={<EditPlanView />}
      />
      <Route path="/plans/my-plans/:planId" element={<AssignedPlanView />} />
      <Route path="/plans/manage-quizzes/new" element={<BuildQuizzesView />} />
      <Route
        path="/plans/manage-quizzes/:quizId"
        element={<BuildQuizzesView />}
      />
      {/*
        TODO: ask about these next two routes going to the same exact content
        */}
      <Route
        path="/plans/manage-plans/:planId/team-member/:teamMemberId"
        element={<TeamMemberProgressView />}
      />
      <Route
        path={`/reports/plans/:planId/team-member/:teamMemberId`}
        element={<TeamMemberProgressView />}
      />
      <Route
        path={`/reports/team-members/:teamMemberId/plan/:planId`}
        element={<TeamMemberProgressView />}
      />
      <Route path="/plans/manage-plans/:planId" element={<PlanAssignees />} />
      <Route
        path="/plans/manage-plans/:planId/train-together"
        element={<TrainingMode />}
      />
      <Route
        path={`/${Constants.ROUTE_PATH_NAMES.HEADLESS_PATH_NAME}/:documentId`}
        element={<SignalDocument />}
      />
      <Route
        path={`/reports/plans/:planId/:compliancePlan?`}
        element={<PlanAssignees reportsRoute />}
      />
      <Route
        path={`/reports/team-members/:teamMemberId`}
        element={<ReportsTeamMembersView />}
      />
      <Route
        path={`/${Constants.ROUTE_PATH_NAMES.COMPLIANCE_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/:documentId/:isCompliance`}
        element={<DocumentRouteHandler />}
      />
      <Route
        path={'/admin/categories/:categoryId'}
        element={<EditCategory />}
      />{' '}
      <Route path={`/admin`} element={<Admin />} />
      <Route path={'/admin/categories'} element={<Admin />} />
      {/* Reports Tabs */}
      <Route element={<ReportsLayout />}>
        <Route path={`/reports/plans`} element={<ReportsPlansTab />} />
        <Route
          path={`/reports/team-members`}
          element={<ReportsTeamMembersTab />}
        />
      </Route>
      {/* Plans Tabs */}
      <Route path="plans">
        <Route element={<PlansLayout />}>
          <Route path="my-plans" element={<MyPlansTab />} />
          <Route path="manage-plans" element={<ManagePlansTab />} />
          <Route path="manage-quizzes" element={<BuildQuizzesTab />} />
        </Route>
      </Route>
      <Route path="/plans/add/:planId" element={<AddReadOnlyPlanPage />} />
      {/* Document Layout */}
      <Route element={<DocumentLayout />}>
        <Route
          path="/plans/my-plans/:planId/doc/:docId"
          element={<DocumentProxy />}
        />
        <Route
          path={`/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/:documentId`}
          element={<DocumentRouteHandler />}
        />
      </Route>
      {/* Explore Layout */}
      <Route element={<ExploreLayout />}>
        <Route index element={<OperationsTab />} />
        <Route
          path={`/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`}
          element={<SearchResultsPage />}
        />
        <Route
          path="/category/:categoryId"
          element={<OperationsSubcategoryPage />}
        />
      </Route>
      {Object.entries(oldPathsForRedirects).map(([path, to]) => (
        <Route key={path} path={path} element={<Redirect to={to} />} />
      ))}
    </NotFoundSwitch>
  </Suspense>
);

// Web uses the okta <Security> wrapper to check for auth
const SecureRouteHandledByWeb: React.FC = () => (
  <Suspense fallback={<Placeholder />}>
    <NotFoundSwitch>
      <Route
        path={`/${Constants.ROUTE_PATH_NAMES.SETTINGS_PATH_NAME}`}
        element={
          <SecureRoute>
            <SettingsPage />
          </SecureRoute>
        }
      />
      <Route
        path={`/${Constants.ROUTE_PATH_NAMES.BINARY_PATH_NAME}/:documentId`}
        element={
          <SecureRoute>
            <PrintView />
          </SecureRoute>
        }
      />
      <Route
        path="/plans/manage-plans/:planId/edit"
        element={
          <SecureRoute>
            <EditPlanView />
          </SecureRoute>
        }
      />
      <Route
        path="/plans/my-plans/:planId"
        element={
          <SecureRoute>
            <AssignedPlanView />
          </SecureRoute>
        }
      />
      <Route
        path="/plans/manage-quizzes/new"
        element={
          <SecureRoute>
            <BuildQuizzesView />
          </SecureRoute>
        }
      />
      <Route
        path="/plans/manage-quizzes/:quizId"
        element={
          <SecureRoute>
            <BuildQuizzesView />
          </SecureRoute>
        }
      />
      <Route
        path="/plans/manage-plans/:planId/team-member/:teamMemberId"
        element={
          <SecureRoute>
            <TeamMemberProgressView />
          </SecureRoute>
        }
      />
      <Route
        path={`/reports/plans/:planId/team-member/:teamMemberId`}
        element={
          <SecureRoute>
            <TeamMemberProgressView />
          </SecureRoute>
        }
      />
      <Route
        path={`/reports/team-members/:teamMemberId/plan/:planId`}
        element={
          <SecureRoute>
            <TeamMemberProgressView />
          </SecureRoute>
        }
      />
      <Route
        path={`/reports/plan/:planId/team-members/:teamMemberId`}
        element={
          <SecureRoute>
            <TeamMemberProgressView />
          </SecureRoute>
        }
      />
      <Route
        path="/plans/manage-plans/:planId"
        element={
          <SecureRoute>
            <PlanAssignees />
          </SecureRoute>
        }
      />
      <Route
        path="/plans/add/:planId"
        element={
          <SecureRoute>
            <AddReadOnlyPlanPage />
          </SecureRoute>
        }
      />
      <Route
        path="/plans/manage-plans/:planId/train-together"
        element={
          <SecureRoute>
            <TrainingMode />
          </SecureRoute>
        }
      />
      <Route
        path={`/${Constants.ROUTE_PATH_NAMES.HEADLESS_PATH_NAME}/:documentId`}
        element={
          <SecureRoute>
            <SignalDocument />
          </SecureRoute>
        }
      />
      <Route
        path={`/reports/plans/:planId/:compliancePlan?`}
        element={
          <SecureRoute>
            <PlanAssignees reportsRoute />
          </SecureRoute>
        }
      />

      <Route
        path={`/reports/team-members/:teamMemberId`}
        element={
          <SecureRoute>
            <ReportsTeamMembersView />
          </SecureRoute>
        }
      />
      <Route
        path={`/${Constants.ROUTE_PATH_NAMES.COMPLIANCE_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/:documentId/:isCompliance`}
        element={
          <SecureRoute>
            <DocumentRouteHandler />
          </SecureRoute>
        }
      />
      <Route
        path={`/admin/categories/:categoryId`}
        element={
          <SecureRoute>
            <EditCategory />
          </SecureRoute>
        }
      />
      <Route
        path={`/admin/categories`}
        element={
          <SecureRoute>
            <Admin />
          </SecureRoute>
        }
      />
      <Route
        path={`/admin`}
        element={
          <SecureRoute>
            <Admin />
          </SecureRoute>
        }
      />

      {/* Reports Tabs */}
      <Route
        element={
          <SecureRoute>
            <ReportsLayout />
          </SecureRoute>
        }
      >
        <Route path={`/reports/plans`} element={<ReportsPlansTab />} />
        <Route
          path={`/reports/team-members`}
          element={<ReportsTeamMembersTab />}
        />
      </Route>

      {/* Plans Tabs */}
      <Route path="plans">
        <Route
          element={
            <SecureRoute>
              <PlansLayout />
            </SecureRoute>
          }
        >
          <Route path="my-plans" element={<MyPlansTab />} />
          <Route path="manage-plans" element={<ManagePlansTab />} />
          <Route path="manage-quizzes" element={<BuildQuizzesTab />} />
        </Route>
      </Route>

      {/* Document Layout */}
      <Route
        element={
          <SecureRoute>
            <DocumentLayout />
          </SecureRoute>
        }
      >
        <Route
          path="/plans/my-plans/:planId/doc/:docId"
          element={<DocumentProxy />}
        />
        <Route
          path={`/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/:documentId`}
          element={<DocumentRouteHandler />}
        />
      </Route>

      {/* Explore Layout */}
      <Route
        element={
          <SecureRoute>
            <ExploreLayout />
          </SecureRoute>
        }
      >
        <Route index element={<OperationsTab />} />
        <Route
          path={`/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`}
          element={<SearchResultsPage />}
        />
        <Route
          path="/category/:categoryId"
          element={<OperationsSubcategoryPage />}
        />
      </Route>
      <Route
        path={Constants.LOGIN_CALLBACK}
        element={<CustomLoginCallback />}
      />
      {Object.entries(oldPathsForRedirects).map(([path, to]) => (
        <Route
          key={path}
          path={path}
          element={
            <SecureRoute>
              <Redirect to={to} />
            </SecureRoute>
          }
        />
      ))}
    </NotFoundSwitch>
  </Suspense>
);

/**
 * Anything to do with the session cannot be selected here!!
 * It will tear down the entire Routes component and cause problems
 */
const Routes: React.FC = () => {
  const [isInitialized] = useAtom(appInitializedAtom);
  const { isNative } = getAuthFromNative();
  const location = useLocation();

  if (!isInitialized) {
    if (!isNative && location.pathname === Constants.LOGIN_CALLBACK) {
      return (
        <NotFoundSwitch>
          <Route
            path={Constants.LOGIN_CALLBACK}
            element={<CustomLoginCallback />}
          />
        </NotFoundSwitch>
      );
    }
    return null;
  }

  return isNative ? (
    <SecureRoutesHandledByNative />
  ) : (
    <SecureRouteHandledByWeb />
  );
};

export default Routes;
